import UserDetails from "@/models/User/UserDetails";
import { ActionTree, MutationTree, GetterTree } from "vuex";
import {
  RepositoryFactory,
  RepositoryTypes,
} from "@/utils/API/RepositoryFactory";
import { SET_USER_DETAILS, SET_AUTH_STATE } from "../MutationTypes";
import router from "@/router";
import UserLogin from "@/models/User/UserLogin";
import {
  FETCH_USER_DETAILS,
  LOGIN_USER,
  LOGOUT_USER,
  IMPERSONATE_USER,
} from "../ActionTypes";
import { UserTypes } from "@/models/User/UserTypes";
import UserRepository from "@/utils/API/UserRepository";

const LoginRepository = RepositoryFactory.get(RepositoryTypes.Login);

export enum AuthStates {
    Idle,
    LoggingIn,
    Success,
    Failed,
    Forbidden
}

export interface AccountState {
  userDetails: UserDetails;
  isLogged: boolean;
  authState: AuthStates;
  authMessage: string;
}

interface AxiosError {
    response?: {
        data: ApiErrorResponse;
        status: number;
        statusText: string;
        headers: any;
    };
    request?: any;
    message: string;
    config?: any;
}

interface ErrorMessage {
    userVisibleMessage: string;
    developerMessage?: string;
    errorCode?: string;
}

interface ApiErrorResponse {
    errorMessages: ErrorMessage[];
    statusCode?: number;
    isError: boolean;
}

const state: AccountState = {
  userDetails: new UserDetails(UserTypes.Guest, "", ""),
  isLogged: false,
  authState: 0,
  authMessage: "",
};

const actions: ActionTree<any, any> = {
  [LOGIN_USER]({ commit, state }, user: UserLogin) {
    commit(SET_AUTH_STATE, {
      authState: AuthStates.LoggingIn,
      authMessage: "",
    });

    return new Promise((resolve, reject) => {
      LoginRepository.login(user)
        .then((response: any) => {
          const userDetails: UserDetails = response.data.result;

          commit(SET_USER_DETAILS, {
            userDetails,
            logged: true,
          });

          commit(SET_AUTH_STATE, {
            authState: AuthStates.Success,
            authMessage: response.statusText,
          });

          resolve(response);
        })
        .catch((err: AxiosError) => {
          if (err.response && err.response.status === 403) {
            const errorMessage = err.response.data.errorMessages[0];
            commit(SET_AUTH_STATE, {
              authState: AuthStates.Forbidden,
              authMessage: errorMessage.userVisibleMessage,
            });
            reject(errorMessage);
          }
          else if (err.response) {
            const errorMessage = err.response.data.errorMessages[0];
            commit(SET_AUTH_STATE, {
              authState: AuthStates.Failed,
              authMessage: errorMessage.userVisibleMessage,
            });
            reject(errorMessage);
          }
          else {
            commit(SET_AUTH_STATE, {
              authState: AuthStates.Failed,
              authMessage: "Network error!",
            });
            reject({ userVisibleMessage: "Network error!" });
          }
        });
    });
  },

  [FETCH_USER_DETAILS]({ commit, state }, payload?: { config?: any }) {
    return new Promise((resolve, reject) => {
      // Allow callers to provide an axios config (for example to suppress
      // the global 401 redirect behaviour). The UserRepository will merge
      // the provided config with defaults.
      const cfg = payload && payload.config ? payload.config : undefined;
      LoginRepository.fetchUserInfo(cfg)
        .then((response: any) => {
          const userDetails: UserDetails = response.data.result;

          commit(SET_USER_DETAILS, {
            userDetails,
            logged: true,
          });

          commit(SET_AUTH_STATE, {
            authState: AuthStates.Success,
            authMessage: response.statusText,
          });

          resolve(response);
        })
        .catch((error: any) => {
          commit(SET_USER_DETAILS, {
            userDetails: new UserDetails(UserTypes.Guest, "", ""),
            logged: false,
          });

          reject(error);
        });
    });
  },

  [LOGOUT_USER]({ commit, state }) {
    return new Promise((resolve, reject) => {
      LoginRepository.logout()
        .then((response: any) => {
          if (response.data.result === null) {
            commit(SET_USER_DETAILS, {
              userDetails: new UserDetails(UserTypes.Guest, "", ""),
              logged: false,
            });

            commit(SET_AUTH_STATE, {
              authState: AuthStates.Idle,
              authMessage: "",
            });

            router.push("/login");
            resolve(response);
          }
          else {
            const userDetails: UserDetails = response.data.result;

            commit(SET_USER_DETAILS, {
              userDetails,
              logged: true,
            });

            commit(SET_AUTH_STATE, {
              authState: AuthStates.Success,
              authMessage: response.statusText,
            });

            router.push("/administration");
            resolve(response);
          }
        })
        .catch((error: any) => {
          // Clear local auth state immediately even on failure
          commit(SET_USER_DETAILS, {
            userDetails: new UserDetails(UserTypes.Guest, "", ""),
            logged: false,
          });
          reject(error);
        });
    });
  },

  [IMPERSONATE_USER]({ commit, state }, id: number) {
    return new Promise((resolve, reject) => {
      UserRepository.impersonateUser({ userId: id })
        .then((response) => {
          const userDetails: UserDetails = response.data.result;

          commit(SET_USER_DETAILS, {
            userDetails,
            logged: true,
          });

          commit(SET_AUTH_STATE, {
            authState: AuthStates.Success,
            authMessage: response.statusText,
          });

          resolve(response);
        })
        .catch((error) => {
          if (error.response) {
            commit(SET_AUTH_STATE, {
              authState: AuthStates.Failed,
              authMessage:
                error.response.data.errorMessages[0].userVisibleMessage,
            });
            reject(error.response.data.errorMessages[0].userVisibleMessage);
          }
          else {
            commit(SET_AUTH_STATE, {
              authState: AuthStates.Failed,
              authMessage: "Network error!",
            });
            reject("Network error");
          }
        });
    });
  },
};

const mutations: MutationTree<AccountState> = {
  // tslint:disable-next-line:no-shadowed-variable
  [SET_USER_DETAILS](
    state,
    { userDetails, logged }: { userDetails: UserDetails; logged: boolean }
  ) {
    state.userDetails = userDetails;
    state.isLogged = logged;
    // @ts-ignore
    // window.Appcues.identify(userDetails.id, {email: userDetails.email});
  },
  // tslint:disable-next-line:no-shadowed-variable
  [SET_AUTH_STATE](
    state,
    { authState, authMessage }: { authState: AuthStates; authMessage: string }
  ) {
    state.authState = authState;
    state.authMessage = authMessage;
  },
};

const getters: GetterTree<AccountState, any> = {
  // tslint:disable-next-line:no-shadowed-variable
  isLoggedIn(state: AccountState): boolean {
    return state.isLogged;
  },

  // tslint:disable-next-line:no-shadowed-variable
  getUserType(state: AccountState): UserTypes {
    if (typeof state.userDetails !== "undefined") {
      return state.userDetails.userType;
    }
    return UserTypes.Guest;
  },
  // tslint:disable-next-line:no-shadowed-variable
  getAuthState(state: AccountState): AuthStates {
    return state.authState;
  },
  // tslint:disable-next-line:no-shadowed-variable
  getAuthMessage(state: AccountState): string {
    return state.authMessage;
  },

  // tslint:disable-next-line:no-shadowed-variable
  getUserDetails(state: AccountState): UserDetails {
    return state.userDetails as UserDetails;
  },
};

export const AccountModule = {
  actions,
  getters,
  mutations,
  state,
};
